<template>
  <div
    id="loader"
    class="ff-mono fs-13 tt-u"
    ref="loader"
    :class="{ 'is-loaded': isLoaded }"
  >
    <div class="loader__col" v-for="i in 7"></div>
  </div>
</template>

<script setup>
const { $gsap: gsap, $Flip } = useNuxtApp();
const route = useRoute();

const isFirstLoad = useFirstLoad();
const isLoaded = useAppLoaded();
const { loadingProgress, onLoadingComplete } = useLoading();
const brandRow = useBrandRow();
const { registerDelayedCall } = useAnimationCleanup();
const { $mq } = useNuxtApp();

function normalizeRouteName(name) {
  if (!name) return '';
  return name.split('___')[0];
}

const loadPageIntroModule = async () => {
  const routeName = normalizeRouteName(route.name);

  if (routeName === 'index') {
    return import('~/transitions/indexEnter');
  }
  if (routeName === 'works') {
    return import('~/transitions/worksEnter');
  }
  if (routeName === 'works-id') {
    return import('~/transitions/worksIdEnter');
  }
  // Fallback to index
  return import('~/transitions/indexEnter');
};

// Lifecycle
onMounted(() => {
  gsap.set('.page', { opacity: 1 });
  gsap.set('.home__reel', { clipPath: 'inset(50%)' });
  gsap.set('#loader', {
    background: 'linear-gradient(#000 50%, rgba(0, 0, 0, 1))',
  });
  gsap.to('#loader', {
    duration: 1,
    delay: 1,
    background: 'linear-gradient(#000 50%, rgba(0, 0, 0, 0.5))',
  });
});

const firstScramble = () => {
  const elements = gsap.utils.toArray('#header [data-scramble]');

  gsap.to('.header__name .blinker', {
    scale: 0,
    duration: 0.5,
    ease: 'expo',
    width: 0,
    margin: 0,
  });

  return gsap.to(elements, {
    scrambleText: {
      text: function () {
        const currentElement = this.targets()[0];
        return currentElement?.dataset?.scramble || '';
      },
    },
    duration: 1,
    ease: 'smooth',
    stagger: {
      from: 'start',
      amount: 0.45,
    },
  });
};

const expandHeader = () => {
  let tl = gsap.timeline();
  tl.to('#header', {
    duration: 1,
    width: '100vw',
    ease: 'smooth',
  });
  return tl;
};

const menuScramble = () => {
  const elements = gsap.utils.toArray('#header [data-scramble-menu]');

  return gsap.to(elements, {
    scrambleText: {
      text: function () {
        const currentElement = this.targets()[0];
        return currentElement?.dataset?.scrambleMenu || '';
      },
    },
    duration: 1,
    ease: 'smooth',
    onComplete: () => {
      elements.forEach(el => {
        el.style.setProperty('--label', `"${el.dataset.scrambleMenu}"`);
      });
    },
    stagger: {
      from: 'start',
      amount: 0.55,
    },
  });
};

const hideIntro = () => {
  let tl = gsap.timeline();

  tl.add(firstScramble());
  !$mq.isMobileOrTablet && tl.add(expandHeader());
  tl.call(() => {
    loadPageIntroModule().then(module => {
      const enterFn = Object.values(module)[0];
      gsap.to('#loader', {
        opacity: 0,
        force3D: true,
        duration: 1,
        onComplete: () => {
          qs('.home__reel video')?.play().catch(error => {
            if (error.name !== 'AbortError') console.warn('[home-reel] Playback failed:', error);
          });
          isFirstLoad.value = false;
        },
      });

      if ($mq.isMobileOrTablet) {
        gsap.to('#header', {
          y: 0,
          duration: 1,
          ease: 'expo.out',
          onComplete: () => {
            brandRow.value = 'top';
          },
        });
      }
      enterFn();
    });
  });
  tl.add(menuScramble(), '<');
};

onLoadingComplete(() => {
  hideIntro();

  const dc = gsap.delayedCall(1.5, () => {
    isLoaded.value = true;
  });
  registerDelayedCall(dc);
});
</script>

<style scoped lang="scss">
#loader {
  cursor: wait;
  display: grid;
  grid-template-columns: repeat(var(--columns), 1fr);
  height: 100vh;
  left: 0;
  opacity: 1;
  overflow: hidden;
  position: fixed;
  top: 0;
  user-select: none;
  width: 100vw;
  z-index: layer(header) - 1;
  backdrop-filter: blur(2rem);
  background: linear-gradient(#000 50%, rgba(0, 0, 0, 1));
}
</style>
